import { Instance } from 'mobx-state-tree';
import moment from 'moment';
import { RouteCalendarSummaryDtoModelBase } from './RouteCalendarSummaryDtoModel.base';
import getFormattedTime from '../../../helpers/getFormattedTime';
import getShiftEndMoment from '../../../helpers/getShiftEndMoment';
import getScaledColor from '../../../helpers/getScaledColor';
import getAppRoot from '../utils/getAppRoot';
import { defaultDateTimeFormat } from '../../../constants';
import { IColor } from '../../../types/IColor';

/* The TypeScript type of an instance of RouteCalendarSummaryDtoModel */
export interface RouteCalendarSummaryDtoModelType extends Instance<typeof RouteCalendarSummaryDtoModel.Type> {}

/* A graphql query fragment builders for RouteCalendarSummaryDtoModel */
export { selectFromRouteCalendarSummaryDto, routeCalendarSummaryDtoModelPrimitives, RouteCalendarSummaryDtoModelSelector } from './RouteCalendarSummaryDtoModel.base';

/**
 * RouteCalendarSummaryDtoModel
 *
 * Lightweight route summary for the calendar view
 */
export const RouteCalendarSummaryDtoModel = RouteCalendarSummaryDtoModelBase
  .views((self) => ({
    get shiftStartTimeMoment(): moment.Moment | undefined {
      return self.startDate && self.shiftStartTime
        ? moment(`${self.startDate} ${self.shiftStartTime}`, defaultDateTimeFormat)
        : undefined;
    },
    get shiftEndTimeMoment(): moment.Moment | undefined {
      return self.startDate && self.shiftEndTime
        ? getShiftEndMoment(self.startDate, self.shiftStartTime, self.shiftEndTime)
        : undefined;
    },
  }))
  .views((self) => ({
    get shiftStartTimeString(): string | undefined {
      return getFormattedTime(self.shiftStartTimeMoment, getAppRoot(self).organization.settings?.dateTimeFormat);
    },
    get shiftEndTimeString(): string | undefined {
      return getFormattedTime(self.shiftEndTimeMoment, getAppRoot(self).organization.settings?.dateTimeFormat);
    },
    get color(): IColor {
      return self.hexColor ? getScaledColor(self.hexColor) : getScaledColor('#000000');
    },
    get driver(): { name: string | null; initials: string; deleted: boolean; invite: null } | null {
      if (!self.driverName) return null;
      const initials = self.driverName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      return {
        name: self.driverName, initials, deleted: false, invite: null,
      };
    },
  }))
  .views(() => ({
    get constraints() { return undefined; },
    get isDeleting() { return false; },
    get isBeingModified() { return false; },
    get isHovered() { return false; },
    get isSelected() { return false; },
    get lateEvent() { return undefined; },
    get finished() { return false; },
    get isOnline() { return undefined as boolean | undefined; },
    get lastLocationUpdateMinutes() { return undefined as number | undefined; },
    get isLocked() { return false; },
    get isReversing() { return false; },
  }));
